import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { inject, Injectable, isDevMode } from '@angular/core'
import { Router } from '@angular/router'
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular'
import { JWTDecode, TokenResponse, TokenStorage } from '@interfaces'
import { jwtDecode } from 'jwt-decode'
import { BehaviorSubject, Observable, map, take } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private http = inject(HttpClient)
	private auth0 = inject(Auth0Service)
	private router = inject(Router)

	private loginParams = isDevMode()
		? {
				apiKey: 'wYL1OSqMQHy0UbEga6A-FUZodesPnTTV-IKGM7RnUHAzAeJUuoMgs83p1czvDoKL',
				clientId: 'OMMkSF9iwxMfbi1U6JZ8dnfla8wRUMFD',
				audience: 'development'
			}
		: {
				apiKey: 'hpckR16edUkBgZerS4ZjR6A6f8WrpLkdY6Wivq69_tZuPXV6eREYRUt70Ao5_FgJ',
				clientId: 'nPK7dx8b0gSIGlxKwCTrHlaYb07ihWZn',
				audience: 'quality_assurance'
			}

	private user = new BehaviorSubject<TokenStorage | null>(null)
	user$ = this.user.asObservable()

	private userAuth0 = new BehaviorSubject<User | null>(null)
	userAuth0$ = this.userAuth0.asObservable()

	private loading = new BehaviorSubject<boolean>(false)
	loading$ = this.loading.asObservable()
	private loginInFlight = false

	isLoggedIn$: Observable<boolean> = this.user$.pipe(map(Boolean))

	constructor() {
		this.loadTokenFromLocalStorage()
		this.loadUser()
	}

	// Maneja el login del usuario
	login() {
		if (this.loginInFlight) {
			console.debug('[auth] Skipping duplicated backend session exchange')
			return
		}

		this.loginInFlight = true
		this.loading.next(true)
		console.debug('[auth] Starting app session exchange', {
			hasStoredToken: Boolean(localStorage.getItem('token'))
		})

		this.auth0
			.getAccessTokenSilently()
			.pipe(take(1))
			.subscribe({
				next: authToken => {
					console.debug('[auth] Received Auth0 access token', {
						hasToken: Boolean(authToken)
					})

					this.getToken(authToken)
						.pipe(take(1))
						.subscribe({
							next: ({ body }) => {
								console.debug('[auth] Backend session token received')
								this.updateSession(body)
								this.loadUser()
								this.router.navigate([''])
								this.loading.next(false)
								this.loginInFlight = false
								console.debug('[auth] App session established', {
									hasStoredToken: Boolean(localStorage.getItem('token'))
								})
							},
							error: (e: HttpErrorResponse) => {
								console.error('[auth] Backend session exchange failed', e)
								this.loading.next(false)
								this.loginInFlight = false
								this.logout()
							}
						})
				},
				error: error => {
					console.error('[auth] Auth0 silent token retrieval failed', error)
					this.loading.next(false)
					this.loginInFlight = false
					this.logout()
				}
			})
	}

	// Desloguea al usuario y borra todo el storage
	logout() {
		console.debug('[auth] Clearing application session', {
			path: window.location.pathname,
			search: window.location.search
		})
		this.user.next(null)
		this.auth0.logout({ logoutParams: { returnTo: window.location.origin } })
		localStorage.clear()
	}

	// Refresca el token si ha caducado
	refreshToken(): Observable<TokenResponse> {
		const token: TokenStorage | null = JSON.parse(localStorage.getItem('token') ?? 'null')
		return this.http.post<TokenResponse>(
			'/login/refresh-token',
			{ refresh_token: token?.refresh_token },
			{
				headers: { 'X-No-Headers': 'true' }
			}
		)
	}

	// Actualiza la información en sesión a partir del token
	updateSession(token: TokenResponse) {
		const decodedToken: JWTDecode = jwtDecode(token.access_token)
		const roles: string[] = decodedToken.scopes ?? []

		const tokenInfo: TokenStorage = {
			...token,
			roles: roles
		}

		this.user.next(tokenInfo)
		localStorage.setItem('token', JSON.stringify(tokenInfo))
		console.debug('[auth] Session token persisted', {
			roles,
			hasRefreshToken: Boolean(token.refresh_token)
		})
	}

	// Genera el token de usuario
	private getToken(authToken: string | undefined): Observable<HttpResponse<any>> {
		return this.http.post<any>(
			'/login/token',
			{ audience: this.loginParams.audience },
			{
				headers: {
					'Content-Type': 'application/json',
					//		'X-Api-Key': this.loginParams.apiKey, Este proyecto no manda api-key por el momento
					Authorization: `Bearer ${authToken}`
				},
				observe: 'response'
			}
		)
	}

	private loadTokenFromLocalStorage() {
		const tokenStorage: TokenStorage = JSON.parse(localStorage.getItem('token')!)
		this.user.next(tokenStorage)
		console.debug('[auth] Loaded token from storage', {
			hasToken: Boolean(tokenStorage)
		})
	}

	private loadUser() {
		const userStorage: User = JSON.parse(localStorage.getItem('user')!)

		if (!userStorage) {
			this.auth0.user$.subscribe(user => {
				this.userAuth0.next(user!)
				localStorage.setItem('user', JSON.stringify(user))
				console.debug('[auth] Loaded Auth0 user from SDK', {
					hasUser: Boolean(user)
				})
			})
		} else {
			this.userAuth0.next(userStorage)
			console.debug('[auth] Loaded Auth0 user from storage', {
				hasUser: Boolean(userStorage)
			})
		}
	}
}
